import { Component, Input, Output, OnDestroy, EventEmitter, Inject } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import { combineLatest, Subject } from 'rxjs';
import { BuyerState } from '~app/modules/generic/store';
import * as fromStoryblok from '~app/modules/storyblok/store';
import { ApplicationEventService } from '~shared/modules/application-event/services';
import { CategoriesNavbarApplicationEventHandler } from '~app/modules/generic/helpers';
import { Category } from '~app/modules/generic/models';
import {
  APP_ALL_OFFERS_ROUTE,
  APP_CATEGORY_ROUTE,
  APP_CUSTOM_PLP_ROUTE,
  APP_LANDING_PAGE_ROUTE,
  APP_PROMO_ROUTE,
  FeatureToggleEnum,
  NUMBER_9,
} from '~shared/constants';
import { StoryData } from 'storyblok-js-client';
import { EditorialList, ThematicResources } from '~app/modules/storyblok/models/storyblok-global-content.model';
import { APP_BASE_HREF } from '@angular/common';
import { Params } from '@angular/router';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { GtmDatalayerService } from '~shared/services/gtm-datalayer.service';
import {
  GaTrackingEventAction,
  GaTrackingEventCategory,
  GaTrackingEventName,
} from '~shared/modules/ga-tracking/constants';
import { SUPER_SAVINGS_CATEGORIES_ID } from '~app/modules/generic/constants/promo-constants';

@Component({
  selector: 'app-promo-categories',
  templateUrl: './promo-categories.component.html',
  styleUrls: ['./promo-categories.component.scss'],
})
export class PromoCategoriesComponent implements OnDestroy {
  @Input() selectedCategory: Category;
  @Output() menuClosed = new EventEmitter<null>();

  public MAX_VISIBLE_PROMOTIONS_MENU_ITEMS_COUNT = NUMBER_9;
  public categoryPath: string = `/${APP_CATEGORY_ROUTE}`;
  public landingPagePath: string = `/${APP_LANDING_PAGE_ROUTE}`;
  public contentPagePath: string = `/${APP_CUSTOM_PLP_ROUTE}`;
  public allOffersLandingPage: string = `${APP_ALL_OFFERS_ROUTE}`;
  public promoContentPage: string = `${APP_PROMO_ROUTE}`;

  private readonly destroySubject = new Subject<boolean>();
  private applicationEventHandler: CategoriesNavbarApplicationEventHandler;

  public editorialList: EditorialList;
  public thematicResources: ThematicResources;

  // TODO: Remove once PROMO PAGE will be available
  superSavingsCategory: Category;
  public isSuperSavingsEnable: boolean = false;

  constructor(
    private store: Store<BuyerState>,
    private translateService: TranslateService,
    applicationEventService: ApplicationEventService,
    @Inject(APP_BASE_HREF) private baseHref: string,
    private readonly featureFlagService: ActiveFeaturesConfigcatService,
    private gtmDataLayerService: GtmDatalayerService
  ) {
    this.applicationEventHandler = new CategoriesNavbarApplicationEventHandler(
      applicationEventService,
      this.translateService
    );

    this.setOfferMenuContent();
  }

  public ngOnInit(): void {
    this.loadSuperSavings();
  }

  // TODO: Remove once PROMO PAGE will be available
  private loadSuperSavings(): void {
    combineLatest(
      this.featureFlagService.isFeatureEnabledForCurrentCountry$(FeatureToggleEnum.FF_SUPER_SAVINGS),
      this.translateService.get('HOME.HOME_PAGE.HEADER.CATEGORIES.SUPER_SAVINGS')
    )
      .pipe(takeUntil(this.destroySubject))
      .subscribe(([isSuperSavingsEnable, translation]) => {
        this.isSuperSavingsEnable = isSuperSavingsEnable;
        if (isSuperSavingsEnable) {
          this.superSavingsCategory = {
            id: SUPER_SAVINGS_CATEGORIES_ID,
            name: translation,
            parents: [],
            children: [],
            slug: '',
          };
        }
      });
  }

  setOfferMenuContent(): void {
    this.store
      .pipe(select(fromStoryblok.getOfferMenuContent), takeUntil(this.destroySubject))
      .subscribe((offerMenuContent: StoryData) => {
        if (offerMenuContent?.content?.global?.length) {
          this.editorialList = offerMenuContent?.content?.global?.[0].editoriallist?.[0];
          this.thematicResources = offerMenuContent?.content?.global?.[0].thematicresources?.[0];
        }
      });
  }

  closeMenu(): void {
    this.menuClosed.emit();
  }

  loadMenuItemClickEvent(label: string) {
    this.gtmDataLayerService.gtagEvent({
      category: GaTrackingEventCategory.Navigation,
      action: GaTrackingEventAction.NavigationClick,
      event: GaTrackingEventName.Navigation,
      GA_eventname: GaTrackingEventName.Interaction,
      label: label,
    });
  }

  private getNavigationLink(path: string): string {
    const baseUrl = `/${this.baseHref.replace(new RegExp('/', 'g'), '')}`;
    return path.startsWith('/') ? `${baseUrl}${path}` : `${baseUrl}/${path}`;
  }

  genericNavigateTo(path: string, queryParams?: Params): void {
    if (!!queryParams && Object.keys(queryParams).length) {
      const parsedParams = Object.entries(queryParams);
      const urlParams: string = parsedParams
        .map((entry: [string, any]) => {
          const [key, value] = entry;
          return `${key}=${value}`;
        })
        .join('&');

      const url: string = this.getNavigationLink(`${path}`);
      window.location.href = `${url}?${urlParams}`;
      return;
    }

    window.location.href = this.getNavigationLink(`${path}`);
  }

  ngOnDestroy(): void {
    this.applicationEventHandler.destroy();
    this.closeMenu();
    this.destroySubject.next();
    this.destroySubject.complete();
  }
}
